import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import Api from "./utils/apiCall";
import { API_URLS } from "./apis/urls";

export interface ISapAuthorizationTCode {
  SrNo?: string;
  TransactionCode: string;
  Remarks: string;
}

export interface ISapMasterOption {
  Value: string;
  Ddtext: string;
}

export interface ISapAuthorizationMasterData {
  Authorization_Revoked?: ISapMasterOption[];
  Purchase_Organizations?: ISapMasterOption[];
  Plants?: ISapMasterOption[];
  Wings?: ISapMasterOption[];
  User_Categories?: ISapMasterOption[];
  SAP_servers?: ISapMasterOption[];
  SAP_Modules?: ISapMasterOption[];
  Business_Areas?: ISapMasterOption[];
  [key: string]: ISapMasterOption[] | any;
}

export interface ISapAuthorizationRequest {
  id?: number | string;
  ReferenceNumber?: string;
  referenceNumber?: string;
  EmpID?: string;
  empId?: string;
  EmpName?: string;
  EmployeeName?: string;
  employeeName?: string;
  Designation?: string;
  Department?: string;
  ContactNumber?: string;
  Wing?: string;
  NewOrExistingUser?: string; // "N" | "E"
  ExistingUserID?: string;
  PurchaseGroup?: string;
  Plant?: string;
  BusinessArea?: string;
  PurchasingOrganization?: string;
  SAPServer?: string;
  SAPModule?: string;
  AuthorizationRevoke?: string;
  Remarks?: string;
  remarks?: string;
  draft?: boolean;
  is_draft?: boolean;
  Action?: string;
  RequestStatus?: string;
  requestStatus?: string;
  Status?: string;
  status?: string;
  createdAt?: string;
  created_at?: string;
  requestCreationDate?: string;
  ApproverUserID?: string;
  ApproverName?: string;
  approverUserID?: string;
  approverName?: string;
  ARemarks?: string;
  HRemarks?: string;
  ToTcodes?: {
    results?: ISapAuthorizationTCode[];
  };
  ToRemarks?: {
    results?: any[];
  } | any[];
  [key: string]: any;
}

interface ISapAuthorizationState {
  listData: any | null;
  masterData: ISapAuthorizationMasterData | null;
  detailData: ISapAuthorizationRequest | null;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action: "list" | "master" | "findOne" | "createUpdate" | "approveReject" | "deleteDraft" | "sync" | null;
}

const initialState: ISapAuthorizationState = {
  listData: null,
  masterData: null,
  detailData: null,
  loading: "idle",
  error: null,
  action: null,
};

// Generic helper for thunk creation
const createApiThunk = (type: string, url: string) =>
  createAsyncThunk(`sapAuthorization/${type}`, async (payload: any = {}, thunkAPI) => {
    try {
      const res = await Api.post(url, payload);
      return res.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(err));
    }
  });

export const fetchSapAuthMasterAll = createApiThunk("masterAll", API_URLS.SAP_AUTHORIZATION.MASTER_FIND_ALL);
export const fetchSapAuthMasterOne = createApiThunk("masterOne", API_URLS.SAP_AUTHORIZATION.MASTER_FIND_ONE);
export const fetchSapAuthRequests = createApiThunk("fetchList", API_URLS.SAP_AUTHORIZATION.FIND_ALL);
export const fetchSapAuthRequestById = createApiThunk("findOne", API_URLS.SAP_AUTHORIZATION.FIND_ONE);
export const fetchSapAuthRequestByRef = createApiThunk("findOneByRef", API_URLS.SAP_AUTHORIZATION.FIND_ONE_BY_REF);
export const createOrUpdateSapAuthRequest = createApiThunk("createUpdate", API_URLS.SAP_AUTHORIZATION.CREATE_UPDATE);
export const approveRejectSapAuthRequest = createApiThunk("approveReject", API_URLS.SAP_AUTHORIZATION.APPROVE_REJECT);
export const deleteDraftSapAuthRequest = createApiThunk("deleteDraft", API_URLS.SAP_AUTHORIZATION.DELETE_DRAFT);
export const syncSapAuthRequests = createApiThunk("sync", API_URLS.SAP_AUTHORIZATION.SYNC);

const sapAuthorizationSlice = createSlice({
  name: "sapAuthorization",
  initialState,
  reducers: {
    resetSapAuthState: (state) => {
      state.loading = "idle";
      state.error = null;
      state.action = null;
    },
    clearSapAuthDetail: (state) => {
      state.detailData = null;
    },
  },
  extraReducers: (builder) => {
    const handlePending = (actionName: ISapAuthorizationState["action"]) => (state: ISapAuthorizationState) => {
      state.loading = "pending";
      state.error = null;
      state.action = actionName;
    };
    const handleRejected = (state: ISapAuthorizationState, action: any) => {
      state.loading = "rejected";
      state.error = action.payload || "An error occurred";
    };

    // Master find-all
    builder
      .addCase(fetchSapAuthMasterAll.pending, handlePending("master"))
      .addCase(fetchSapAuthMasterAll.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.masterData = action.payload?.data?.data || action.payload?.data || action.payload;
      })
      .addCase(fetchSapAuthMasterAll.rejected, handleRejected);

    // Master find-one
    builder
      .addCase(fetchSapAuthMasterOne.pending, handlePending("master"))
      .addCase(fetchSapAuthMasterOne.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        const incoming = action.payload?.data?.data || action.payload?.data || action.payload;
        state.masterData = { ...(state.masterData || {}), ...incoming };
      })
      .addCase(fetchSapAuthMasterOne.rejected, handleRejected);

    // List requests
    builder
      .addCase(fetchSapAuthRequests.pending, handlePending("list"))
      .addCase(fetchSapAuthRequests.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        state.listData = action.payload?.data || action.payload;
      })
      .addCase(fetchSapAuthRequests.rejected, handleRejected);

    // Find one
    builder
      .addCase(fetchSapAuthRequestById.pending, handlePending("findOne"))
      .addCase(fetchSapAuthRequestById.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        const raw = action.payload?.data?.data || action.payload?.data || action.payload;
        state.detailData = Array.isArray(raw) ? raw[0] : raw;
      })
      .addCase(fetchSapAuthRequestById.rejected, handleRejected);

    // Find one by ref
    builder
      .addCase(fetchSapAuthRequestByRef.pending, handlePending("findOne"))
      .addCase(fetchSapAuthRequestByRef.fulfilled, (state, action) => {
        state.loading = "fulfilled";
        const raw = action.payload?.data?.data || action.payload?.data || action.payload;
        state.detailData = Array.isArray(raw) ? raw[0] : raw;
      })
      .addCase(fetchSapAuthRequestByRef.rejected, handleRejected);

    // Create / Update
    builder
      .addCase(createOrUpdateSapAuthRequest.pending, handlePending("createUpdate"))
      .addCase(createOrUpdateSapAuthRequest.fulfilled, (state) => {
        state.loading = "fulfilled";
      })
      .addCase(createOrUpdateSapAuthRequest.rejected, handleRejected);

    // Approve / Reject
    builder
      .addCase(approveRejectSapAuthRequest.pending, handlePending("approveReject"))
      .addCase(approveRejectSapAuthRequest.fulfilled, (state) => {
        state.loading = "fulfilled";
      })
      .addCase(approveRejectSapAuthRequest.rejected, handleRejected);

    // Delete draft
    builder
      .addCase(deleteDraftSapAuthRequest.pending, handlePending("deleteDraft"))
      .addCase(deleteDraftSapAuthRequest.fulfilled, (state) => {
        state.loading = "fulfilled";
      })
      .addCase(deleteDraftSapAuthRequest.rejected, handleRejected);
  },
});

export const { resetSapAuthState, clearSapAuthDetail } = sapAuthorizationSlice.actions;
export default sapAuthorizationSlice.reducer;
